import { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { api } from '../services/api';
import './PDFViewer.css';

// Configure PDF.js worker - use local worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface PDFViewerProps {
  documentUrl: string;
  onViewed?: () => void;
}

function PDFViewer({ documentUrl, onViewed }: PDFViewerProps) {
  const [pdfData, setPdfData] = useState<{ data: Uint8Array } | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1.0);

  useEffect(() => {
    loadPdfUrl();
  }, [documentUrl]);

  const loadPdfUrl = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get session token from URL
      const path = window.location.pathname;
      const token = path.split('/').pop();


      if (!token) {
        throw new Error('Token de session manquant');
      }

      // Le PDF transite par l'API : pas d'appel direct au stockage depuis le navigateur (CORS)
      const response = await api.post('/session/document/file', { token }, { responseType: 'arraybuffer' });
      setPdfData({ data: new Uint8Array(response.data) });
    } catch (err: any) {
      console.error('[PDFViewer] Failed to load PDF URL:', err);
      setError(err.message || 'Impossible de charger le document');
    } finally {
      setLoading(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('[PDFViewer] PDF load error:', error);
    setError('Erreur lors du chargement du PDF: ' + error.message);
  };

  const handleNextPage = () => {
    if (currentPage < numPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 2.5));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleConfirmViewed = () => {
    if (onViewed) {
      onViewed();
    }
  };

  if (loading) {
    return (
      <div className="border-2 border-gray-300 rounded-lg bg-white p-8 min-h-[600px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-2 border-red-300 rounded-lg bg-red-50 p-8 min-h-[600px] flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-red-800 mb-2">Erreur</h3>
          <p className="text-red-700">{error}</p>
          <button
            onClick={loadPdfUrl}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between bg-gray-100 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomOut}
            disabled={scale <= 0.5}
            className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            title="Zoom arrière"
          >
            −
          </button>
          <span className="text-sm text-gray-700 font-medium px-2">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={handleZoomIn}
            disabled={scale >= 2.5}
            className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            title="Zoom avant"
          >
            +
          </button>
        </div>

        <span className="text-sm text-gray-700 font-medium">
          Page {currentPage} sur {numPages}
        </span>
      </div>

      {/* PDF Viewer */}
      <div className="border-2 border-gray-300 rounded-lg bg-gray-100 overflow-auto">
        <div className="flex justify-center py-4">
          {pdfData && (
            <Document
              file={pdfData}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Chargement du document...</p>
                </div>
              }
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={
                  <div className="bg-white p-20 rounded shadow">
                    <div className="animate-pulse text-gray-400">
                      Chargement de la page {currentPage}...
                    </div>
                  </div>
                }
              />
            </Document>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between bg-gray-100 rounded-lg p-4">
        <button
          onClick={handlePreviousPage}
          disabled={currentPage <= 1}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ← Précédent
        </button>

        <span className="text-sm text-gray-700 font-medium">
          Page {currentPage} sur {numPages}
        </span>

        <button
          onClick={handleNextPage}
          disabled={currentPage >= numPages}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Suivant →
        </button>
      </div>

      {/* Confirmation - Show after reaching last page */}
      {currentPage === numPages && numPages > 0 && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-2 text-lg">
            ✓ Avez-vous lu l'intégralité du document ?
          </h3>
          <p className="text-sm text-blue-700 mb-4">
            Veuillez confirmer que vous avez pris connaissance de toutes les pages avant de continuer.
          </p>
          <button
            onClick={handleConfirmViewed}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            J'ai lu et compris le document
          </button>
        </div>
      )}
    </div>
  );
}

export default PDFViewer;
